import React from 'react';
import { theme } from '../styles/theme';
import Container from '../components/ui/Container';
import FooterPageContent from '../components/layout/FooterPageContent';

const TermsAndConditionsPage = () => {
  const pageStyles = {
    padding: `${theme.spacing.xxl} 0`,
    backgroundColor: theme.colors.background,
    minHeight: '100vh',
  };

  const contentStyles = {
    maxWidth: '900px',
    margin: '0 auto',
    padding: `0 ${theme.spacing.lg}`,
  };

  const headingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xxl,
    fontWeight: theme.typography.fontWeight.bold,
    color: theme.colors.primary,
    marginBottom: theme.spacing.lg,
    textAlign: 'center',
  };

  const subHeadingStyles = {
    fontFamily: theme.typography.fontFamily.primary,
    fontSize: theme.typography.fontSize.xl,
    fontWeight: theme.typography.fontWeight.semibold,
    color: theme.colors.primaryDark,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.md,
  };

  const paragraphStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
  };

  const listStyles = {
    fontSize: theme.typography.fontSize.base,
    lineHeight: theme.typography.lineHeight.relaxed,
    color: theme.colors.text,
    marginBottom: theme.spacing.md,
    paddingLeft: theme.spacing.xl,
  };

  const contactBoxStyles = {
    backgroundColor: theme.colors.neutral,
    padding: theme.spacing.lg,
    borderRadius: theme.borderRadius.md,
    marginTop: theme.spacing.xl,
    marginBottom: theme.spacing.lg,
  };

  const lastUpdatedStyles = {
    fontSize: theme.typography.fontSize.sm,
    color: theme.colors.textSecondary,
    textAlign: 'center',
    marginBottom: theme.spacing.xl,
    fontStyle: 'italic',
  };

  return (
    <FooterPageContent slug="terms">
    <div style={pageStyles}>
      <Container>
        <div style={contentStyles}>
          <h1 style={headingStyles}>BS Trading Co. – Terms & Conditions</h1>
          <p style={lastUpdatedStyles}>Last Updated: January 2026</p>

          <p style={paragraphStyles}>
            Welcome to BS Trading Co. ("Company," "we," "us," or "our"). By accessing or using our website, placing an order, 
            or engaging with our services, you agree to be bound by the following Terms & Conditions. Please read them carefully.
          </p>

          <p style={paragraphStyles}>
            If you do not agree with these Terms, please discontinue use of our site and services.
          </p>

          <h2 style={subHeadingStyles}>1. Company Information</h2>
          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co.</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> 817-447-4433
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> 1-800-554-0910
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
          </div>

          <h2 style={subHeadingStyles}>2. Use of Our Website</h2>
          <p style={paragraphStyles}>By using our website, you agree to:</p>
          <ul style={listStyles}>
            <li>Use our services only for lawful purposes</li>
            <li>Not engage in fraudulent, harmful, or abusive behavior</li>
            <li>Not interfere with website functionality or security</li>
            <li>Provide accurate, up-to-date account information (if applicable)</li>
          </ul>
          <p style={paragraphStyles}>
            We reserve the right to terminate access for users who violate these terms.
          </p>

          <h2 style={subHeadingStyles}>3. Product Availability & Descriptions</h2>
          <p style={paragraphStyles}>We strive to keep all product information accurate; however:</p>
          <ul style={listStyles}>
            <li>Product descriptions, pricing, and availability may change without notice</li>
            <li>Colors and product details may appear differently depending on your device</li>
            <li>We reserve the right to limit quantities or discontinue products at any time</li>
            <li>We are not liable for typographical or display errors.</li>
          </ul>

          <h2 style={subHeadingStyles}>4. Pricing & Payment</h2>
          <p style={paragraphStyles}>
            All prices are listed in U.S. Dollars unless stated otherwise.
          </p>
          <p style={paragraphStyles}>By placing an order, you agree that:</p>
          <ul style={listStyles}>
            <li>You authorize BS Trading Co. to charge the provided payment method</li>
            <li>We use Stripe as our secure payment processor</li>
            <li>Orders may be cancelled or placed on hold if payment cannot be verified</li>
            <li>Taxes, fees, and shipping charges may apply based on your location.</li>
          </ul>

          <h2 style={subHeadingStyles}>5. Order Acceptance & Cancellation</h2>
          <p style={paragraphStyles}>We reserve the right to:</p>
          <ul style={listStyles}>
            <li>Accept or decline any order</li>
            <li>Cancel orders due to pricing errors, stock issues, or suspected fraud</li>
            <li>Request additional verification for large or unusual orders</li>
          </ul>
          <p style={paragraphStyles}>
            If an order is cancelled after payment has been processed, you will receive a refund to your original payment method.
          </p>

          <h2 style={subHeadingStyles}>6. Shipping & Delivery</h2>
          <p style={paragraphStyles}>
            Shipping times and fees vary depending on your location and selected shipping method.
          </p>
          <p style={paragraphStyles}>Please note:</p>
          <ul style={listStyles}>
            <li>Delivery dates are estimates and not guaranteed</li>
            <li>Delays caused by carriers, weather, or external factors are outside our control</li>
            <li>Once an order is shipped, risk of loss transfers to the customer</li>
          </ul>
          <p style={paragraphStyles}>
            For shipping questions, contact us at <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
          </p>

          <h2 style={subHeadingStyles}>7. Returns, Exchanges & Refunds</h2>
          <p style={paragraphStyles}>
            Our return policy outlines eligible items, timelines, and procedures. In general:
          </p>
          <ul style={listStyles}>
            <li>Items must be unused and in original packaging</li>
            <li>Some items may be non-returnable due to safety or custom order restrictions</li>
            <li>Customers are responsible for return shipping unless the item arrived damaged or incorrect</li>
          </ul>
          <p style={paragraphStyles}>
            A full return policy can be provided or added upon request.
          </p>

          <h2 style={subHeadingStyles}>8. Account Registration (If Applicable)</h2>
          <p style={paragraphStyles}>
            Certain features may require an account. You are responsible for:
          </p>
          <ul style={listStyles}>
            <li>Keeping your login information secure</li>
            <li>Maintaining accuracy of your account details</li>
            <li>All activity under your account</li>
          </ul>
          <p style={paragraphStyles}>
            We may suspend or close accounts for policy violations.
          </p>

          <h2 style={subHeadingStyles}>9. Tax Exemption Certificates & Business Information</h2>
          <p style={paragraphStyles}>
            If you submit a resale certificate or tax exemption certificate to qualify for tax-exempt purchases, you agree that:
          </p>
          <ul style={listStyles}>
            <li>You are authorized to provide such certificates on behalf of your business</li>
            <li>All information provided is accurate, current, and valid</li>
            <li>You will use tax-exempt purchases only for qualified resale purposes as permitted by applicable state law</li>
            <li>You are responsible for any misuse of tax exemption status and related tax liabilities</li>
          </ul>
          
          <h3 style={{ ...subHeadingStyles, fontSize: theme.typography.fontSize.lg, marginTop: theme.spacing.lg }}>
            Storage and Security of Tax Information
          </h3>
          <p style={paragraphStyles}>
            We collect and store tax exemption certificates and related business information (including resale certificate numbers, 
            business tax IDs, and state tax permit numbers) for the following purposes:
          </p>
          <ul style={listStyles}>
            <li>Verifying eligibility for tax-exempt purchases</li>
            <li>Maintaining compliance with state sales tax regulations</li>
            <li>Providing documentation during tax audits or inquiries</li>
            <li>Processing orders and calculating applicable taxes</li>
          </ul>
          
          <p style={paragraphStyles}>
            <strong>Security Measures:</strong> All tax exemption certificates and related documents are stored securely using 
            industry-standard encryption and access controls. Certificate files are stored in private, encrypted cloud storage 
            and are accessible only to authorized administrative personnel. Access URLs to certificate documents expire after 
            one hour and each access is logged for security purposes.
          </p>
          
          <p style={paragraphStyles}>
            <strong>Legal Basis:</strong> We collect and retain this information as necessary for:
          </p>
          <ul style={listStyles}>
            <li>Compliance with state and federal tax laws</li>
            <li>Fulfillment of our contractual obligations to you</li>
            <li>Our legitimate business interests in maintaining accurate tax records</li>
          </ul>
          
          <p style={paragraphStyles}>
            <strong>Retention:</strong> Tax exemption certificates and related business information will be retained for the period 
            required by applicable tax laws (typically 3-7 years depending on jurisdiction) or until you request deletion, whichever 
            is longer. You may request updates to your certificate information at any time by contacting us.
          </p>
          
          <p style={paragraphStyles}>
            <strong>Your Responsibilities:</strong> You must notify us immediately if:
          </p>
          <ul style={listStyles}>
            <li>Your tax exemption certificate expires or is revoked</li>
            <li>Your business information changes (name, address, tax ID)</li>
            <li>You are no longer eligible for tax-exempt purchases</li>
            <li>You suspect unauthorized use of your tax exemption status</li>
          </ul>
          
          <p style={paragraphStyles}>
            Failure to maintain valid, current tax exemption documentation may result in charges for applicable sales tax on 
            past and future orders.
          </p>

          <h2 style={subHeadingStyles}>10. Intellectual Property</h2>
          <p style={paragraphStyles}>
            All content on our website—including images, logos, product descriptions, graphics, and text—is the property 
            of BS Trading Co. or its licensors and is protected by copyright and trademark laws.
          </p>
          <p style={paragraphStyles}>You may not:</p>
          <ul style={listStyles}>
            <li>Copy or reproduce website content</li>
            <li>Use our images or branding without permission</li>
            <li>Reverse-engineer or scrape our website data</li>
          </ul>

          <h2 style={subHeadingStyles}>11. Limitation of Liability</h2>
          <p style={paragraphStyles}>To the fullest extent permitted by law:</p>
          <ul style={listStyles}>
            <li>BS Trading Co. is not responsible for indirect, incidental, or consequential damages</li>
            <li>We make no guarantees regarding website availability or uninterrupted service</li>
            <li>Our total liability for any claim shall not exceed the amount paid for the product or service in question</li>
          </ul>

          <h2 style={subHeadingStyles}>12. Warranties Disclaimer</h2>
          <p style={paragraphStyles}>
            All products and services are provided "as-is" without warranties of any kind except where required by law.
          </p>
          <p style={paragraphStyles}>We disclaim all implied warranties including:</p>
          <ul style={listStyles}>
            <li>Merchantability</li>
            <li>Fitness for a particular purpose</li>
            <li>Non-infringement</li>
          </ul>

          <h2 style={subHeadingStyles}>13. Indemnification</h2>
          <p style={paragraphStyles}>
            You agree to indemnify and hold harmless BS Trading Co., its owners, employees, and partners from any claims, 
            losses, damages, or expenses arising from:
          </p>
          <ul style={listStyles}>
            <li>Your use of our website</li>
            <li>Your violation of these Terms</li>
            <li>Your infringement of any rights of a third party</li>
          </ul>

          <h2 style={subHeadingStyles}>14. Changes to These Terms</h2>
          <p style={paragraphStyles}>
            We may modify or update these Terms at any time. Changes take effect immediately when posted to the website. 
            Continued use of our services signifies acceptance of the updated Terms.
          </p>

          <h2 style={subHeadingStyles}>15. Governing Law</h2>
          <p style={paragraphStyles}>
            These Terms are governed by the laws of the State of Texas, without regard to conflict-of-law principles. 
            Any disputes will be handled in the appropriate courts located in Tarrant County, Texas.
          </p>

          <h2 style={subHeadingStyles}>16. Contact Us</h2>
          <p style={paragraphStyles}>For questions about these Terms & Conditions:</p>
          <div style={contactBoxStyles}>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>BS Trading Co.</strong>
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>253 Loy St</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>Burleson, TX 76028</p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Phone:</strong> 817-447-4433
            </p>
            <p style={{ ...paragraphStyles, marginBottom: theme.spacing.xs }}>
              <strong>Toll-Free:</strong> 1-800-554-0910
            </p>
            <p style={{ ...paragraphStyles, marginBottom: 0 }}>
              <strong>Email:</strong> <a href="mailto:Orders@BSTrading.com" style={{ color: theme.colors.primary }}>Orders@BSTrading.com</a>
            </p>
          </div>
        </div>
      </Container>
    </div>
    </FooterPageContent>
  );
};

export default TermsAndConditionsPage;

